import { DeleteOutlined } from '@ant-design/icons';
import { Button, Form, Input, message } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import AttachFileIcon from 'icons/attach-file.svg';
import { ActionTypes, From, Message, UserPrompt } from '../vitrual-assistant.type';
import ChatMessageAi from './chat-message-ai';
import { ChatTyping } from './chat-typing';
import { localize } from '/react4xp/mixins/helpers';
import { askAIStream, stopAIStream } from '../actions';
import { readAiStream } from '../ai-stream';
import { AiAttachment, AiStep } from '../vitrual-assistant.type';
import { AI_ASSISTANT_ACCEPTED_FILE_TYPES } from '../virtual-assistant.constants';
import {
    formatFileSize,
    isAiAssistantFileSizeAllowed,
    isAllowedAiAssistantFile,
} from '../virtual-assistant.utils';

const GUEST_AI_LIMIT = 10;
const GUEST_AI_STORAGE_KEY = 'nj-keden-guest-request-count';

const getGuestRequestCount = (): number => {
    try {
        const raw = localStorage.getItem(GUEST_AI_STORAGE_KEY);
        const parsed = Number(raw ?? 0);

        if (!Number.isFinite(parsed)) return GUEST_AI_LIMIT;

        return Math.min(
            GUEST_AI_LIMIT,
            Math.max(0, Math.floor(parsed))
        );
    } catch {
        return GUEST_AI_LIMIT;
    }
};

interface ChatProps {
    messages: Message[];
    updateMessage: (id: number, patch: Partial<Message>) => void;
    emailOperator: () => void;
    addMessage: (...messages: Array<Omit<Message, 'id'> & { id?: number }>) => Promise<void> | void;
    isTyping?: boolean;
    translations: any;
    isAuthenticated: boolean;
}

const Chat = ({
    addMessage,
    messages,
    emailOperator,
    isTyping,
    translations,
    updateMessage,
    isAuthenticated
}: ChatProps) => {
    const [form] = Form.useForm<UserPrompt>();
    const [isWaitingAnswer, setIsWaitingAnswer] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const threadIdRef = useRef<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const messageIdRef = useRef<string | null>(null);
    const isRequestActiveRef = useRef(false);
    const [isStopping, setIsStopping] = useState(false);
    const [guestRequestCount, setGuestRequestCount] =
        useState<number>(getGuestRequestCount);

    const isGuestLimitReached =
        !isAuthenticated && guestRequestCount >= GUEST_AI_LIMIT;


    useEffect(() => {
        return () => {
            const messageId = messageIdRef.current;

            if (messageId && isRequestActiveRef.current) {
                void stopAIStream(messageId).catch(error => {
                    console.error('Failed to stop AI on unmount:', error);
                });
            }

            abortControllerRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        const syncGuestCount = (event: StorageEvent) => {
            if (
                event.key === GUEST_AI_STORAGE_KEY ||
                event.key === null
            ) {
                setGuestRequestCount(getGuestRequestCount());
            }
        };

        window.addEventListener('storage', syncGuestCount);

        return () => {
            window.removeEventListener('storage', syncGuestCount);
        };
    }, []);

    useEffect(() => {
        const queueMessage = (e: Event) => {
            const customEvent = e as CustomEvent<{ query: string }>;
            handleUserPrompt({ query: customEvent.detail?.query || '' });
        };

        window.addEventListener('assistantMessage', queueMessage as EventListener);

        return () => {
            window.removeEventListener('assistantMessage', queueMessage as EventListener);
        };
    }, [isWaitingAnswer]);

    const showArticle = (message: any) => {
        const { payload } = message?.action || {};
    };

    const actions: Record<ActionTypes, (message?: any) => void> = {
        [ActionTypes.SHOW_ARTICLE]: showArticle,
        [ActionTypes.EMAIL_OPERATOR]: emailOperator,
        [ActionTypes.TELEGRAM_BOT]: () => location.replace('https://t.me/online_chat_help_bot'),
        [ActionTypes.CONSULTATIONS]: () => location.replace('https://t.me/SalyqBot?start=poll9'),
    };

    const resetSelectedFile = () => {
        setSelectedFile(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (!file) return;

        if (!isAllowedAiAssistantFile(file)) {
            message.error(localize('validation.file-format-not-supported', translations));
            resetSelectedFile();
            return;
        }

        if (!isAiAssistantFileSizeAllowed(file)) {
            message.error(localize('validation.file-size-exceeded', translations));
            resetSelectedFile();
            return;
        }

        setSelectedFile(file);
    };

    const handleStopGeneration = async () => {
        if (!isRequestActiveRef.current || isStopping) return;

        setIsStopping(true);

        const messageId = messageIdRef.current;
        const controller = abortControllerRef.current;

        try {
            if (messageId) {
                await stopAIStream(messageId);
            }
        } catch (error) {
            console.error('Failed to stop AI generation:', error);
            message.error('Не удалось отправить команду остановки');
        } finally {
            controller?.abort();
            setIsStopping(false);
        }
    };

    const handleUserPrompt = async (values: UserPrompt) => {
        const query = values?.query ?? form.getFieldValue('query');

        const trimmedQuery =
            typeof query === 'string' ? query.trim() : '';

        if (!trimmedQuery || isRequestActiveRef.current) return;

        if (trimmedQuery.length > 4000) {
            message.error('Вопрос не должен превышать 4000 символов');
            return;
        }

        if (!isAuthenticated) {
            const currentCount = getGuestRequestCount();

            if (currentCount >= GUEST_AI_LIMIT) {
                setGuestRequestCount(currentCount);
                message.warning(
                    'Лимит 10 запросов исчерпан. Авторизуйтесь для продолжения.'
                );
                return;
            }
        }

        if (!isAuthenticated) {
            const nextCount = Math.min(
                GUEST_AI_LIMIT,
                getGuestRequestCount() + 1
            );

            try {
                localStorage.setItem(
                    GUEST_AI_STORAGE_KEY,
                    String(nextCount)
                );
            } catch {
                message.error('Не удалось сохранить лимит запросов');
                return;
            }

            setGuestRequestCount(nextCount);
        }

        isRequestActiveRef.current = true;

        messageIdRef.current = null;

        const fileToSend = selectedFile || undefined;
        const loadingId = Date.now();
        const startedAt = Date.now();

        const controller = new AbortController();
        abortControllerRef.current = controller;

        let answerText = '';
        let attachments: AiAttachment[] = [];
        let steps: AiStep[] = [];

        let completed = false;
        let stopped = false;
        let streamError: string | null = null;

        await addMessage({
            from: From.USER,
            text: trimmedQuery,
        });

        form.resetFields();
        setIsWaitingAnswer(true);

        // Одно сообщение, которое будем обновлять по мере стриминга
        void addMessage({
            id: loadingId,
            from: From.ASSISTANT,
            text: '',
            loading: true,
            status: 'streaming',
            startedAt,
            attachments: [],
            steps: [],
        });

        try {
            const response = await askAIStream({
                query: trimmedQuery,
                threadId: threadIdRef.current || undefined,
                file: fileToSend,
                signal: controller.signal,
            });

            await readAiStream(response, event => {
                switch (event.type) {
                    case 'start': {
                        const threadId = event.messageMetadata?.threadId;

                        if (threadId) {
                            threadIdRef.current = threadId;
                        }

                        if (event.messageId) {
                            messageIdRef.current = event.messageId;
                        }

                        break;
                    }


                    case 'data-step': {
                        const existing = steps.find(item => item.id === event.id);

                        const step: AiStep = {
                            id: event.id,
                            node: event.data.node ?? existing?.node,
                            displayName: event.data.displayName ?? existing?.displayName,
                            status: event.data.status,
                            durationMs: event.data.durationMs ?? existing?.durationMs,
                            startedAt: existing?.startedAt ?? Date.now(),
                        };

                        if (existing) {
                            steps = steps.map(item =>
                                item.id === step.id ? step : item
                            );
                        } else {
                            steps = [...steps, step];
                        }

                        updateMessage(loadingId, {
                            steps: [...steps],
                            status: 'streaming',
                        });

                        break;
                    }


                    case 'text-delta': {
                        answerText += event.delta;

                        updateMessage(loadingId, {
                            text: answerText,
                            loading: false,
                            status: 'streaming',
                        });

                        break;
                    }

                    case 'data-attachment': {
                        const attachment: AiAttachment = {
                            fileId: event.data.file_id,
                            name: event.data.name,
                            url: event.data.url,
                        };

                        attachments = [
                            ...attachments.filter(
                                item => item.fileId !== attachment.fileId
                            ),
                            attachment,
                        ];

                        updateMessage(loadingId, {
                            attachments: [...attachments],
                            loading: false,
                        });

                        break;
                    }

                    case 'finish': {
                        completed = true;
                        updateMessage(loadingId, {
                            finishedAt: Date.now(),
                            loading: false,
                            status: 'completed',
                        });

                        break;
                    }

                    case 'abort': {
                        stopped = true;
                        updateMessage(loadingId, {
                            finishedAt: Date.now(),
                            loading: false,
                            status: 'stopped',
                        });

                        break;
                    }

                    case 'error': {
                        streamError = event.errorText || 'agent_error';

                        updateMessage(loadingId, {
                            loading: false,
                            status: 'error',
                            finishedAt: Date.now(),
                        });

                        break;
                    }

                    default:
                        break;
                }
            });

            if (streamError) {
                throw new Error(streamError);
            }

            if (!completed && !stopped) {
                throw new Error('Stream finished without terminal event');
            }
            updateMessage(loadingId, {
                text: answerText,
                attachments: [...attachments],
                steps: [...steps],
                loading: false,
                status: stopped ? 'stopped' : 'completed',
                finishedAt: Date.now(),
            });
        } catch (error) {
            const wasAborted = controller.signal.aborted;

            console.error('AI stream error:', error);

            updateMessage(loadingId, {
                text: answerText || (
                    wasAborted
                        ? 'Ответ остановлен'
                        : localize('chat-error-getting-answer', translations)
                ),
                attachments: [...attachments],
                steps: [...steps],
                loading: false,
                status: wasAborted ? 'stopped' : 'error',
                finishedAt: Date.now(),
            });

            if (!wasAborted) {
                message.error('Не удалось получить ответ ИИ');
            }
        } finally {
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }
            messageIdRef.current = null;
            isRequestActiveRef.current = false;
            setIsWaitingAnswer(false);
            resetSelectedFile();
        }
    };


    return (
        <>
            <ul className="messages-container">
                {isTyping && <ChatTyping />}

                {messages?.map((message, index) => {
                    const currentFrom = message.from;
                    const prevMessage = messages[index - 1];
                    const prevFrom = prevMessage?.from;

                    const showAvatar = currentFrom !== prevFrom;
                    const messageAction = message.action;
                    const action = messageAction
                        ? {
                            action: () => actions[messageAction.type](message),
                        }
                        : {};

                    return (
                        <ChatMessageAi
                            showAvatar={showAvatar}
                            key={message.id}
                            message={message}
                            translations={translations}
                            {...action}
                        />
                    );
                })}
            </ul>

            {isGuestLimitReached && (
    <div className="assistant-ai_guest_limit assistant-ai_guest_limit--blocked">
        <strong>Лимит запросов исчерпан</strong>
        <span>
            Вы использовали 10 запросов.
            Авторизуйтесь на портале, чтобы продолжить
            пользоваться ИИ-ассистентом.
        </span>
    </div>
)}

            <Form
                layout="vertical"
                form={form}
                onFinish={handleUserPrompt}
                className="assistant-ai_form"
            >
                {selectedFile && (
                    <div className="assistant-ai_file">
                        <div className="assistant-ai_file_info">
                            <span className="assistant-ai_file_name">{selectedFile.name}</span>
                            <span className="assistant-ai_file_size">
                                {formatFileSize(selectedFile.size)}
                            </span>
                        </div>
                        <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            disabled={isWaitingAnswer}
                            onClick={resetSelectedFile}
                            aria-label={localize('file.delete', translations)}
                        />
                    </div>
                )}

                <div className="assistant-ai_form_body">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={AI_ASSISTANT_ACCEPTED_FILE_TYPES}
                        onChange={handleFileChange}
                        className="assistant-ai_file_input"
                        style={{ display: 'none' }}
                    />

                    <Form.Item
                        name="query"
                        className="query"
                        style={{ flex: 1, minWidth: 0 }}
                    >
                        <Input.TextArea
                            required
                            disabled={isWaitingAnswer || isGuestLimitReached}
                            className="assistant-ai_query_input"
                            placeholder={
                                isGuestLimitReached
                                    ? 'Авторизуйтесь, чтобы продолжить'
                                    : isWaitingAnswer
                                        ? localize('chat-assistant-answering', translations)
                                        : localize('placeholders.query', translations)
                            }
                            autoSize={{ minRows: 4, maxRows: 4 }}
                            onKeyDown={event => {
                                if (
                                    event.key !== 'Enter' ||
                                    event.shiftKey ||
                                    event.nativeEvent.isComposing
                                ) {
                                    return;
                                }

                                event.preventDefault();

                                if (
                                    isWaitingAnswer ||
                                    isRequestActiveRef.current ||
                                    isGuestLimitReached
                                ) {
                                    return;
                                }

                                const query = form.getFieldValue('query');

                                if (typeof query !== 'string' || !query.trim()) {
                                    return;
                                }

                                form.submit();
                            }}
                        />
                    </Form.Item>
                </div>

                <div className="assistant-ai_form_actions">
                    <Button
                        type="text"
                        icon={<AttachFileIcon />}
                        disabled={isWaitingAnswer || isGuestLimitReached}
                        onClick={() => fileInputRef.current?.click()}
                        title={localize('button.attach-file', translations)}
                        aria-label={localize('button.attach-file', translations)}
                        className="assistant-ai_attach_button"
                    >
                        {localize('button.attach-file', translations)}
                    </Button>

                    {isWaitingAnswer ? (
                        <Button
                            type="primary"
                            danger
                            loading={isStopping}
                            onClick={handleStopGeneration}
                            className="assistant-ai_stop_button"
                        >
                            Остановить
                        </Button>
                    ) : (
                        <Button
                            htmlType="submit"
                            type="primary"
                            disabled={isGuestLimitReached}
                            className="assistant-ai_send_button"
                        >
                            {localize('button.send', translations)}
                        </Button>
                    )}
                </div>
            </Form>
        </>
    );
};

export default Chat;
